import { useState } from "react";
import { MessageSquare, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { hapticLight, hapticSuccess, hapticError } from "@/utils/haptic";

export default function SendMessageButton({ 
  recipientId, 
  recipientName, 
  currentUser,
  trackTitle,
  variant = "icon", // "icon" | "button"
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleOpenDialog = () => {
    if (!currentUser) {
      hapticError();
      toast.error("Please sign in to send messages");
      return;
    }
    
    if (currentUser.id === recipientId) {
      hapticError();
      toast.error("You can't message yourself");
      return;
    }
    
    hapticLight();
    setIsOpen(true);
    // Pre-fill with track context if available
    if (trackTitle) {
      setMessageText(`Hey! I came across your track "${trackTitle}" and wanted to reach out about a potential collaboration or feature. `);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      hapticError();
      toast.error("Please enter a message");
      return;
    }

    setIsSending(true);
    try {
      // Insert message directly into Supabase
      const { error } = await supabase
        .from("messages")
        .insert({
          sender_id: currentUser.id,
          recipient_id: recipientId,
          content: messageText.trim()
        });

      if (error) throw error;

      hapticSuccess();
      toast.success(`Message sent to ${recipientName}!`);
      setMessageText("");
      setIsOpen(false);
      
      // Dispatch event to refresh messages in Layout
      window.dispatchEvent(new CustomEvent('messageSent', { 
        detail: { recipientId, recipientName } 
      }));
    } catch (error) {
      console.error("Error sending message:", error);
      hapticError();
      toast.error(error.message || "Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  if (variant === "button") {
    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleOpenDialog}
                className={`neuro-base active:neuro-pressed rounded-xl px-3 py-2 flex items-center gap-2 ${className}`}
              >
                <MessageSquare className="w-4 h-4 text-[#a0a0a0]" />
                <span className="text-[#a0a0a0] text-sm">Message</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Send a message to {recipientName}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <MessageDialog
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          recipientName={recipientName}
          messageText={messageText}
          setMessageText={setMessageText}
          isSending={isSending}
          handleSendMessage={handleSendMessage}
        />
      </>
    );
  }

  // Icon variant (default)
  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleOpenDialog}
              className={`p-2 rounded-xl neuro-base hover:neuro-pressed transition-all ${className}`}
            >
              <MessageSquare className="w-4 h-4 text-[#808080]" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Message {recipientName}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <MessageDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        recipientName={recipientName}
        messageText={messageText}
        setMessageText={setMessageText}
        isSending={isSending}
        handleSendMessage={handleSendMessage}
      />
    </>
  );
}

function MessageDialog({
  isOpen,
  setIsOpen,
  recipientName,
  messageText,
  setMessageText,
  isSending,
  handleSendMessage
}) {
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-[#1a1a1a] border border-[#2a2a2a] w-[95vw] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#d0d0d0] flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Message {recipientName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <Textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Write your message... Ask about collaborations, features, or just say hi!"
            className="bg-[#252525] border-[#3a3a3a] text-[#d0d0d0] placeholder:text-[#606060] min-h-[120px] resize-none"
          />
          
          <div className="flex gap-3 justify-end">
            <Button
              onClick={() => setIsOpen(false)}
              variant="ghost"
              className="text-[#808080] hover:text-[#a0a0a0]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendMessage}
              disabled={!messageText.trim() || isSending}
              className="neuro-base active:neuro-pressed rounded-xl px-4 py-2 flex items-center gap-2"
            >
              {isSending ? (
                <div className="w-4 h-4 border-2 border-[#505050] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4 text-[#a0a0a0]" />
              )}
              <span className="text-[#a0a0a0]">{isSending ? "Sending..." : "Send"}</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
